import { UpdateChildCompany, updateChildCompanySchema } from "@elton/types";
import { SheetClose, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@elton/ui/core";

import { LoadingButton } from "@/components/buttons/loading-button";
import { InputField } from "@/components/fields/input-field";

import { FormRenderState } from "@/pages/company-details-page/company-details-page.tsx";

import { useChildCompanyPartialForm } from "./use-child-company-partial-form.tsx";

import { T, useT } from "@transifex/react";

interface IProps {
  disabled?: boolean;
  data: UpdateChildCompany;
  onUpdate: (data: UpdateChildCompany) => void;
  state: FormRenderState | null;
  loading: boolean;
}

/**
 * This form component is different from the standard CompanyForm.
 * Instead of displaying the entire form, it conditionally renders specific attributes.
 * This approach is beneficial for making partial updates to the company entity, such as on the company details page.
 */
export const ChildCompanyPartialForm: React.FC<IProps> = ({
  disabled,
  data,
  state,
  onUpdate,
  loading,
}) => {
  const t = useT();
  const form = useChildCompanyPartialForm({
    data,
    onSubmit: onUpdate,
  });

  const handleFormSubmit = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void form.handleSubmit();
  };

  return (
    <form.Subscribe
      children={() => (
        <>
          <SheetHeader>
            <SheetTitle>
              <T _str="Update company" />
            </SheetTitle>
            <SheetDescription>
              <T _str="Update company - Click the button when you're done." />
            </SheetDescription>
          </SheetHeader>
          <form className="grid grid-cols-12 gap-6 text-primary">
            {state === "KEY" && (
              <>
                <form.Field
                  name="name"
                  validators={{
                    onBlur: updateChildCompanySchema.shape.name,
                  }}
                  children={(field) => (
                    <InputField
                      field={field}
                      className="col-span-full"
                      label={t("Company Name")}
                      placeholder={t("My company")}
                      disabled={disabled}
                    />
                  )}
                />
                <form.Field
                  name="organizationNumber"
                  validators={{
                    onBlur: updateChildCompanySchema.shape.organizationNumber,
                  }}
                  children={(field) => (
                    <InputField
                      field={field}
                      className="col-span-full"
                      label={t("Organisation Number")}
                      placeholder="xxxxxxxxx"
                      disabled={disabled}
                    />
                  )}
                />
                <form.Field
                  name="contactName"
                  validators={{
                    onBlur: updateChildCompanySchema.shape.contactName,
                  }}
                  children={(field) => (
                    <InputField
                      field={field}
                      className="col-span-full"
                      label={t("Contact Name")}
                      placeholder="John Smith"
                      disabled={disabled}
                    />
                  )}
                />
                <form.Field
                  name="contactPhone"
                  validators={{
                    onBlur: updateChildCompanySchema.shape.contactPhone,
                  }}
                  children={(field) => (
                    <InputField
                      field={field}
                      className="col-span-full"
                      label={t("Contact Phone")}
                      placeholder="+47xxxxxxxx"
                      disabled={disabled}
                    />
                  )}
                />
                <form.Field
                  name="contactEmail"
                  validators={{
                    onBlur: updateChildCompanySchema.shape.contactEmail,
                  }}
                  children={(field) => (
                    <InputField
                      field={field}
                      className="col-span-full"
                      label={t("Contact Email")}
                      placeholder="contact@mycompany.com"
                      disabled={disabled}
                    />
                  )}
                />
              </>
            )}
            {state == "ADDRESS" && (
              <>
                <form.Field
                  name="address"
                  validators={{
                    onBlur: updateChildCompanySchema.shape.address,
                  }}
                  children={(field) => (
                    <InputField
                      field={field}
                      className="col-span-full"
                      label={t("Street Address")}
                      placeholder="Edvard Storms gate 2"
                      disabled={disabled}
                    />
                  )}
                />
                <form.Field
                  name="postalCode"
                  validators={{
                    onBlur: updateChildCompanySchema.shape.postalCode,
                  }}
                  children={(field) => (
                    <InputField
                      field={field}
                      className="col-span-full md:col-span-6"
                      label={t("Postal Code")}
                      placeholder="0166"
                      disabled={disabled}
                    />
                  )}
                />
                <form.Field
                  name="city"
                  validators={{
                    onBlur: updateChildCompanySchema.shape.city,
                  }}
                  children={(field) => (
                    <InputField
                      field={field}
                      className="col-span-full md:col-span-6"
                      label={t("City")}
                      placeholder="Oslo"
                      disabled={disabled}
                    />
                  )}
                />
                <form.Field
                  name="country"
                  validators={{
                    onBlur: updateChildCompanySchema.shape.country,
                  }}
                  children={(field) => (
                    <InputField
                      field={field}
                      className="col-span-full"
                      label={t("Country")}
                      placeholder="Norway"
                      disabled={disabled}
                    />
                  )}
                />
              </>
            )}
            <SheetFooter className="col-span-full">
              <SheetClose asChild>
                <LoadingButton loading={loading} onClick={handleFormSubmit} type="submit">
                  <T _str="Save" />
                </LoadingButton>
              </SheetClose>
            </SheetFooter>
          </form>
        </>
      )}
    />
  );
};
